import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { CircleNotch } from '@phosphor-icons/react';
import { DigitSummaryTab } from './DigitSummaryTab';
import { OverallDigitDistribution } from './OverallDigitDistribution';
import { useDrawsUpdated } from '../../hooks/useDrawsUpdated';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ballLabels = { ball_1: 'Ball 1', ball_2: 'Ball 2', ball_3: 'Ball 3', ball_4: 'Ball 4', ball_5: 'Ball 5' };

/**
 * Embeddable content-only component — renders the Digit Pattern tables/tabs
 * without any outer page chrome (no `min-h-screen`, no page header).
 * W51's Digit Pattern Analysis tab. Owns its fetch and internal sub-tabs.
 */
export function DigitPatternContent() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [nextData, setNextData] = useState(null);
  const [nextLoading, setNextLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');

  useEffect(() => {
    if (activeTab === 'sequences' && !nextData && !nextLoading) {
      setNextLoading(true);
      axios.get(`${API}/analysis/digit-pattern/next-digits`)
        .then(r => setNextData(r.data))
        .catch(() => toast.error('Failed to fetch next-digit predictions'))
        .finally(() => setNextLoading(false));
    }
  }, [activeTab, nextData, nextLoading]);

  const fetchDistribution = useCallback(async () => {
      try { const res = await axios.get(`${API}/analysis/digit-pattern`); setData(res.data); }
      catch { toast.error('Failed to fetch digit pattern'); }
      finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchDistribution(); }, [fetchDistribution]);
  useDrawsUpdated(fetchDistribution);

  const tabs = [
    { id: 'summary', label: 'Digit Summary' },
    { id: 'overall', label: 'Overall' },
    ...Object.entries(ballLabels).map(([k, l]) => ({ id: k, label: l })),
    { id: 'sequences', label: 'Sequences' },
  ];

  if (loading) return (
    <div className="bg-white border border-gray-200 rounded-lg p-10 flex items-center justify-center" data-testid="digit-pattern-loading">
      <CircleNotch size={32} className="animate-spin text-blue-600" />
    </div>
  );

  if (!data || data.error) return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500" data-testid="digit-pattern-empty">
      No digit pattern data available.
    </div>
  );

  return (
    <div className="space-y-4" data-testid="digit-pattern-content">
      <Toaster position="top-right" richColors />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 flex-wrap" data-testid="digit-pattern-subtabs">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`px-3 py-1.5 rounded text-xs font-bold transition ${activeTab === t.id ? 'bg-[#002FA7] text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
              data-testid={`tab-${t.id}`}>{t.label}</button>
          ))}
        </div>
        <div className="text-[10px] text-gray-400" data-testid="digit-pattern-history-counts">
          Historical: {data.hist_draws} draws | 2026: {data.cy_draws} draws
        </div>
      </div>

      {activeTab === 'summary' && <DigitSummaryTab distribution={data} />}

      {/* Overall Tab */}
      {activeTab === 'overall' && <OverallDigitDistribution data={data} />}

      {/* Per-Ball Tabs */}
      {Object.entries(ballLabels).map(([key, label]) => (
        activeTab === key && (
          <div key={key} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`tab-content-${key}`}>
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-900">{label} — Digit Distribution: Historical vs 2026</h3>
              <p className="text-[10px] text-gray-400">How often each last digit appears at this position. Diff shows 2026 deviation from historical.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="px-4 py-2 text-center font-bold text-gray-500">Digit</th>
                    <th className="px-4 py-2 text-center font-bold text-gray-500">Hist Count</th>
                    <th className="px-4 py-2 text-center font-bold text-gray-500">Hist %</th>
                    <th className="px-4 py-2 text-center font-bold text-blue-600">2026 Count</th>
                    <th className="px-4 py-2 text-center font-bold text-blue-600">2026 %</th>
                    <th className="px-4 py-2 text-center font-bold text-gray-500">Diff</th>
                    <th className="px-4 py-2 text-center font-bold text-gray-500">Status</th>
                    <th className="px-4 py-2 text-left font-bold text-gray-500">Comparison</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.per_ball?.[key] || []).map(ds => {
                    const maxPct = Math.max(...(data.per_ball?.[key] || []).map(x => Math.max(x.hist_pct, x.cy_pct)), 1);
                    const status = ds.cy_count === 0 ? 'MISSING' : ds.diff < -5 ? 'LACKING' : ds.diff > 5 ? 'SURPLUS' : 'NORMAL';
                    const statusColor = {
                      MISSING: 'bg-red-100 text-red-700',
                      LACKING: 'bg-amber-100 text-amber-700',
                      SURPLUS: 'bg-emerald-100 text-emerald-700',
                      NORMAL: 'bg-gray-100 text-gray-500',
                    };
                    return (
                      <tr key={ds.digit} className={`border-b border-gray-100 hover:bg-gray-50 ${status === 'MISSING' ? 'bg-red-50' : ''}`}>
                        <td className="px-4 py-2.5 text-center">
                          <span className="inline-flex w-8 h-8 rounded-full bg-[#002FA7] text-white items-center justify-center font-mono font-bold">{ds.digit}</span>
                        </td>
                        <td className="px-4 py-2.5 text-center font-mono text-gray-600">{ds.hist_count}</td>
                        <td className="px-4 py-2.5 text-center font-mono text-gray-600">{ds.hist_pct}%</td>
                        <td className="px-4 py-2.5 text-center font-mono font-bold text-blue-700">{ds.cy_count}</td>
                        <td className="px-4 py-2.5 text-center font-mono font-bold text-blue-700">{ds.cy_pct}%</td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${ds.diff > 2 ? 'bg-emerald-100 text-emerald-700' : ds.diff < -2 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                            {ds.diff > 0 ? '+' : ''}{ds.diff}%
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${statusColor[status]}`}>{status}</span>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="w-full bg-gray-100 rounded-full h-3 relative">
                            <div className="absolute h-3 rounded-full bg-gray-300" style={{ width: `${(ds.hist_pct / maxPct) * 100}%` }} />
                            <div className="absolute h-3 rounded-full bg-blue-500 opacity-70" style={{ width: `${(ds.cy_pct / maxPct) * 100}%` }} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )
      ))}

      {/* Sequences Tab */}
      {activeTab === 'sequences' && (
        <div className="space-y-4">
          {/* ===== Next-Digit Predictor (what followed this digit sequence historically) ===== */}
          <div className="bg-white border-2 border-indigo-300 rounded-lg overflow-hidden" data-testid="next-digit-predictor">
            <div className="px-4 py-3 bg-gradient-to-r from-indigo-50 to-violet-50 border-b border-indigo-200">
              <h3 className="font-bold text-indigo-900 text-sm">Next-Digit Predictor — What historically followed this digit sequence?</h3>
              <p className="text-[10px] text-gray-500 mt-0.5">
                Finds historical draws whose 5-digit last-digit pattern matches the current draw, then aggregates the digit patterns of the draws that followed.
              </p>
            </div>
            {nextLoading && (
              <div className="p-6 text-center text-gray-500 text-xs">
                <CircleNotch size={24} className="animate-spin inline-block text-indigo-500" /> Scanning history...
              </div>
            )}
            {nextData && !nextData.error && (
              <div className="p-4 space-y-4">
                {/* Pivot header */}
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Pivot draw</div>
                  <div className="text-sm font-mono font-bold text-gray-900">#{nextData.pivot_draw_number}</div>
                  <div className="text-xs text-gray-500">{nextData.pivot_draw_date}</div>
                  <div className="flex gap-1.5 ml-2">
                    {(nextData.pivot_balls || []).map((b, i) => (
                      <span key={i} className="inline-flex flex-col items-center">
                        <span className="inline-flex w-9 h-9 rounded-full bg-gray-100 text-gray-900 items-center justify-center font-mono font-bold text-xs">{b}</span>
                        <span className="text-[9px] text-indigo-600 font-mono font-bold mt-0.5">d{b % 10}</span>
                      </span>
                    ))}
                  </div>
                  <div className="ml-auto text-[10px] text-gray-400 italic">Scanned {nextData.total_draws_scanned} prior draws</div>
                </div>

                {/* Tier cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {(nextData.tiers || []).map(tier => {
                    const hasData = tier.matches > 0;
                    const topNext = hasData ? tier.next_digit_freq.slice(0, 10) : [];
                    const maxPct = hasData ? Math.max(...topNext.map(x => x.pct), 1) : 1;
                    // Strength: highlight digits that exceed uniform (10%) meaningfully
                    const strong = hasData ? topNext.filter(x => x.pct >= 15) : [];
                    return (
                      <div key={tier.tier} className={`rounded-lg border overflow-hidden ${hasData ? 'border-indigo-200 bg-white' : 'border-gray-200 bg-gray-50'}`} data-testid={`tier-${tier.tier}`}>
                        <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex items-center gap-2 flex-wrap">
                          <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-black ${hasData ? 'bg-indigo-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
                            {tier.matches} {tier.matches === 1 ? 'match' : 'matches'}
                          </span>
                          <div className="flex-1">
                            <div className="text-xs font-bold text-gray-900">{tier.label}</div>
                            <div className="text-[10px] text-gray-500">{tier.description}</div>
                          </div>
                        </div>

                        {!hasData && (
                          <div className="p-3 text-[11px] text-gray-500 italic">No historical matches found for this tier.</div>
                        )}

                        {hasData && (
                          <div className="p-3 space-y-3">
                            {/* Top next digits */}
                            <div>
                              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                                Next-Draw Digit Frequency {strong.length > 0 && <span className="text-emerald-600 ml-1">· {strong.length} strong signal{strong.length > 1 ? 's' : ''}</span>}
                              </div>
                              <div className="space-y-1">
                                {topNext.slice(0, 6).map(nd => {
                                  const isStrong = nd.pct >= 15;
                                  const isWeak = nd.pct < 8;
                                  return (
                                    <div key={nd.digit} className="flex items-center gap-2 text-[11px]">
                                      <span className={`inline-flex w-6 h-6 rounded-full items-center justify-center font-mono font-bold text-[11px] ${isStrong ? 'bg-emerald-500 text-white' : isWeak ? 'bg-gray-200 text-gray-500' : 'bg-indigo-100 text-indigo-800'}`}>{nd.digit}</span>
                                      <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                                        <div className={`h-2 rounded-full ${isStrong ? 'bg-emerald-500' : isWeak ? 'bg-gray-300' : 'bg-indigo-400'}`} style={{ width: `${(nd.pct / maxPct) * 100}%` }} />
                                      </div>
                                      <span className={`font-mono font-bold w-12 text-right ${isStrong ? 'text-emerald-700' : 'text-gray-600'}`}>{nd.pct}%</span>
                                      <span className="text-[10px] text-gray-400 w-10 text-right">({nd.count})</span>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="text-[9px] text-gray-400 mt-1 italic">Uniform baseline = 10% · ≥15% = strong lift</div>
                            </div>

                            {/* Per-position top digits (only for higher-confidence tiers) */}
                            {tier.matches >= 5 && tier.next_per_position && (
                              <div>
                                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1.5">Most Likely Digit at Each Position</div>
                                <div className="grid grid-cols-5 gap-1">
                                  {tier.next_per_position.map(pp => (
                                    <div key={pp.position} className="bg-gray-50 rounded p-1.5 text-center">
                                      <div className="text-[9px] text-gray-500 mb-0.5">B{pp.position}</div>
                                      {pp.top_digits.slice(0, 2).map((td, ti) => (
                                        <div key={ti} className="flex items-center justify-center gap-1 text-[10px]">
                                          <span className={`inline-flex w-5 h-5 rounded-full items-center justify-center font-mono font-bold text-[10px] ${ti === 0 ? 'bg-indigo-500 text-white' : 'bg-indigo-100 text-indigo-700'}`}>{td.digit}</span>
                                          <span className="text-gray-600 font-mono">{td.pct}%</span>
                                        </div>
                                      ))}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Most-recent sample matches */}
                            {tier.samples && tier.samples.length > 0 && (
                              <details className="text-[11px]">
                                <summary className="cursor-pointer font-bold text-indigo-700 hover:text-indigo-900">
                                  View {tier.samples.length} most-recent historical match{tier.samples.length > 1 ? 'es' : ''}
                                </summary>
                                <div className="mt-2 overflow-x-auto">
                                  <table className="w-full text-[10px]">
                                    <thead className="bg-gray-50 text-gray-500">
                                      <tr>
                                        <th className="px-2 py-1 text-left font-bold">Match Draw</th>
                                        <th className="px-2 py-1 text-left font-bold">Match Digits</th>
                                        <th className="px-2 py-1 text-left font-bold">Next Draw</th>
                                        <th className="px-2 py-1 text-left font-bold">Next Digits</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {tier.samples.map((s, si) => (
                                        <tr key={si} className="border-b border-gray-100">
                                          <td className="px-2 py-1">
                                            <div className="font-mono font-bold text-gray-700">#{s.match_draw}</div>
                                            <div className="text-gray-400">{s.match_date}</div>
                                          </td>
                                          <td className="px-2 py-1">
                                            <div className="flex gap-0.5">
                                              {s.match_digits.map((md, mi) => (
                                                <span key={mi} className="inline-flex w-4 h-4 rounded-full bg-gray-200 items-center justify-center font-mono font-bold text-[9px] text-gray-700">{md}</span>
                                              ))}
                                            </div>
                                          </td>
                                          <td className="px-2 py-1">
                                            <div className="font-mono font-bold text-emerald-700">#{s.next_draw}</div>
                                            <div className="text-gray-400">{s.next_date}</div>
                                          </td>
                                          <td className="px-2 py-1">
                                            <div className="flex gap-0.5">
                                              {s.next_digits.map((nd, ni) => (
                                                <span key={ni} className="inline-flex w-4 h-4 rounded-full bg-emerald-100 items-center justify-center font-mono font-bold text-[9px] text-emerald-700">{nd}</span>
                                              ))}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </details>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Consolidated Top Pick */}
                {(() => {
                  // Prefer stronger tier that has data (exact_multiset > subset_4 > subset_3)
                  const order = ['exact_ordered', 'exact_multiset', 'subset_4', 'subset_3'];
                  const useful = (nextData.tiers || []).filter(t => t.matches > 0);
                  if (useful.length === 0) return null;
                  // Pick the most specific tier with ≥5 matches; fall back to any match
                  const primary = useful.find(t => (t.tier === 'exact_multiset' || t.tier === 'exact_ordered') && t.matches > 0)
                    || useful.find(t => t.matches >= 5)
                    || useful[0];
                  const top3 = primary.next_digit_freq.slice(0, 3);
                  return (
                    <div className="mt-2 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-300 rounded-lg p-3" data-testid="next-digit-verdict">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700">🎯 Verdict — Based on {primary.label}</div>
                          <div className="text-[10px] text-gray-500">{primary.matches} historical match{primary.matches > 1 ? 'es' : ''} analyzed</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider">Top 3 next digits:</span>
                          {top3.map((td, i) => (
                            <div key={td.digit} className="flex items-center gap-1">
                              <span className="inline-flex w-7 h-7 rounded-full bg-emerald-500 text-white items-center justify-center font-mono font-black text-sm">{td.digit}</span>
                              <span className="text-[11px] font-mono font-bold text-emerald-700">{td.pct}%</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>

          {/* ===== Existing last-30 sequences table ===== */}
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="seq-table">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-bold text-gray-900">Last 30 Draws — Digit Sequences</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-center font-bold text-gray-500">Draw</th>
                    <th className="px-3 py-2 text-center font-bold text-gray-500">Date</th>
                    <th className="px-3 py-2 text-center font-bold text-gray-500">B1</th>
                    <th className="px-3 py-2 text-center font-bold text-gray-500">B2</th>
                    <th className="px-3 py-2 text-center font-bold text-gray-500">B3</th>
                    <th className="px-3 py-2 text-center font-bold text-gray-500">B4</th>
                    <th className="px-3 py-2 text-center font-bold text-gray-500">B5</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.sequences || []).map((s, idx) => (
                    <tr key={s.draw} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-1.5 border-b font-mono text-center font-bold text-gray-700">#{s.draw}</td>
                      <td className="px-3 py-1.5 border-b text-center text-gray-500">{s.date}</td>
                      {s.digits.map((dg, i) => (
                        <td key={i} className="px-3 py-1.5 border-b text-center">
                          <span className={`inline-flex w-8 h-8 rounded-full items-center justify-center font-mono font-bold text-sm ${dg === 0 ? 'bg-red-100 text-red-800' : dg === 5 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>{dg}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
